import React, { useState, useContext, useEffect } from "react"
import PropTypes from "prop-types"
import { makeStyles } from "@material-ui/core/styles"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import { green, red, orange } from "@material-ui/core/colors"
import {
  Button,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Input,
  MenuItem,
  CircularProgress,
} from "@material-ui/core"
import { getPutSignedUrl, putFileS3, parseToCSV, saveParsed } from "../api/apiCalls"
import { UserContext } from "../contexts/UserContext"
import { HistoryContext } from "../contexts/HistoryContext"
import { MessagingContext } from "../contexts/MessagingContext"

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: 25,
  },
  card: {
    "& > *": {
      margin: theme.spacing(1),
    },
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    spacing: 3,
    marginBottom: 50,
  },
  error: {
    color: red[500],
  },
  "@keyframes blinker": {
    from: { opacity: 0.3 },
    to: { opacity: 1 },
  },
  parsing: {
    color: orange[500],
    position: "absolute",
    left: 310,
    top: 0,
    animationName: "$blinker",
    animationDuration: ".5s",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
  },
  successMessage: {
    color: green[500],
    fontSize: "0.8em",
    marginTop: 10,
    maxWidth: 300,
  },
  input: {
    display: "none",
  },
  detail: {
    fontSize: 12,
    fontStyle: "italic",
  },
  uploadBtn: {
    position: "absolute",
    right: 10,
    top: 10,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 300,
  },
  button: {
    marginTop: 20,
    minWidth: 300,
    color: "white",
    maxWidth: 300,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  buttonDisabled: {
    color: "rgba(0,0,0,0.2)",
  },
  done: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -3,
    marginLeft: -12,
  },
  buttonProgressValue: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: 0,
    marginLeft: -7,
  },
  wrapper: {
    position: "relative",
  },
  quarterRadioLabel: {
    marginTop: 10,
  },
  successMessageHolder: {
    minHeight: 50,
  },
}))

const QuarterlyUploadFileButton = ({
  label,
  callBack,
  maxQuarters,
  context,
  disabled,
}) => {
  const [transferring, setTransferring] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [inputKey, setInputKey] = useState("")
  const [quarter, setQuarter] = useState(null)
  const [quarterNumber, setQuarterNumber] = useState(1)
  const [year, setYear] = useState("")
  const [selectionLabel, setSelectionLabel] = useState("")
  const [error, setError] = useState()
  const classes = useStyles()
  const { user } = useContext(UserContext)
  const { fetchHistory, saveActivity, saveError } = useContext(HistoryContext)
  const { addErrorMessage, addSuccessMessage } = useContext(MessagingContext)

  const availableQuarters = ["Q1", "Q2", "Q3", "Q4"]
  const availableYears = ["2020", "2021", "2022", "2023", "2024", "2025"]

  useEffect(() => {
    if (!(year && quarter && quarterNumber)) {
      setSelectionLabel("")
      return
    }
    let quarterString = `${quarter}-${year}`
    if (quarterNumber > 1) {
      let additionalQuarters = parseInt(quarterNumber, 10) - 1
      let currYear = year
      for (let i = 0; i < additionalQuarters; i++) {
        let nextQuarter =
          availableQuarters[
            (availableQuarters.indexOf(quarter) + i + 1) % availableQuarters.length
          ]
        if (nextQuarter === "Q1") {
          currYear++
        }
        quarterString += `:${nextQuarter}-${currYear}`
      }
    }
    setSelectionLabel(quarterString)
  }, [year, quarter, quarterNumber, availableQuarters])

  const changeInputKey = () => {
    let randomString = Math.random().toString(36)
    setInputKey(randomString)
  }

  const handleQuarterChange = (event) => {
    setQuarter(event.target.value)
  }

  const handleYearChange = (event) => {
    setYear(event.target.value)
  }

  const handleQuarterNumberChange = (event) => {
    setQuarterNumber(event.target.value)
  }

  const postProcessing = () => {
    setTransferring(false)
    setParsing(false)
    fetchHistory(user)
  }

  const createQuarterNumberOptions = () => {
    const numberOptions = []
    for (let i = 0; i < maxQuarters; i++) {
      numberOptions.push(
        <FormControlLabel
          key={`${i + 1}`}
          value={`${i + 1}`}
          control={<Radio color="primary" />}
          label={`${i + 1}`}
          labelPlacement="start"
        />
      )
    }

    return numberOptions
  }

  const uploadFile = (event) => {
    setTransferring(true)
    getPutSignedUrl(
      user,
      `quarterly-${context}XXXX${selectionLabel}`,
      event.target.files[0]
    ).then(({ url, key, file }) => {
      // add activity feed
      putFileS3(url, file, file.type)
        .then(async (response) => {
          changeInputKey()
          console.log("put file s3 response: ", response)
          if (response.status === 200) {
            console.log("put file s3 passed status 200")
            setParsing(true)
            parseToCSV(user, context, key.split("/")[2]).then(
              async (parsedResponse) => {
                console.log("parseToCSV response: ", parsedResponse)
                if (parsedResponse.statusCode === 200) {
                  console.log("parseToCSV passed status 200")
                  await new Promise((resolve) => setTimeout(resolve, 3000))
                  saveParsed(
                    user,
                    context,
                    quarter + "-" + year,
                    parsedResponse.filepath
                  ).then(async (resp) => {
                    console.log(">>>>>>>>>>>>resp check: ", resp.test)
                    console.log(">>>>>>>>>>>>is payload? : ", resp.Payload)
                    if (resp.Payload && resp.Payload.startsWith("INFO:  200")) {
                      console.log("saved parsed passed status 200")
                      addSuccessMessage("UPLOAD", `${file.name} Upload Complete`)
                      await saveActivity(
                        user,
                        file.name,
                        true,
                        `${context} ${selectionLabel}`,
                        key.split("/")[2],
                        resp.Payload
                      )
                    } else {
                      //addErrorMessage("ERROR was found with", resp.Payload)
                      await saveError(
                        user,
                        file.name,
                        false,
                        context,
                        true,
                        resp.Payload
                      )
                      postProcessing()
                    }
                    setYear("")
                    setQuarter("")
                    if (maxQuarters > 1) {
                      setQuarterNumber("")
                    }
                    postProcessing()
                  })
                } else {
                  addErrorMessage(
                    "ERROR",
                    `${parsedResponse.status} ${parsedResponse.statusText}`
                  )
                  await saveError(user, file.name, false, context, true)
                  postProcessing()
                }
              }
            )
          } else {
            addErrorMessage("ERROR", `${response.status} ${response.statusText}`)
            await saveError(user, file.name, false, context, true)
            postProcessing()
          }

          if (callBack) {
            callBack()
          }
        })
        .catch((error) => {
          setError(error)
        })
    })
  }

  return (
    <div className={classes.root}>
      <FormControl className={classes.formControl}>
        <input
          key={inputKey}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          className={classes.input}
          id={`upload-${context}`}
          multiple
          disabled={disabled}
          onChange={(e) => {
            if (!disabled) {
              uploadFile(e)
            } else {
              e.preventDefault()
            }
          }}
          type="file"
        />
        <FormLabel>Year</FormLabel>
        <Select
          labelId="id-start-year"
          id="select-start-year"
          value={year}
          onChange={handleYearChange}
          input={<Input />}
        >
          {availableYears.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
        <FormLabel className={classes.quarterRadioLabel}>Quarter</FormLabel>
        <RadioGroup
          aria-label="quarter"
          row
          name="quarter"
          value={quarter}
          onChange={handleQuarterChange}
        >
          {availableQuarters.map((quarter) => (
            <FormControlLabel
              key={quarter}
              value={quarter}
              control={<Radio color="primary" />}
              label={quarter}
              labelPlacement="start"
            />
          ))}
        </RadioGroup>
        {quarterNumber > 1 && (
          <>
            <FormLabel className={classes.quarterRadioLabel}>
              Number of Consecutive Quarters
            </FormLabel>

            <RadioGroup
              aria-label="quarter"
              row
              name="quarterNumber"
              value={quarterNumber}
              onChange={handleQuarterNumberChange}
            >
              {createQuarterNumberOptions()}
            </RadioGroup>
          </>
        )}
        <FormLabel>Query: {selectionLabel && selectionLabel}</FormLabel>
        {error && <div className={classes.error}>{error}</div>}
        {parsing && <div className={classes.parsing}>parsing</div>}
        <label htmlFor={`upload-${context}`}>
          <div className={classes.wrapper}>
            <Button
              className={classes.button}
              variant={transferring ? "outlined" : "contained"}
              color="primary"
              component={`spam${context}`}
              disableElevation
              disabled={
                transferring || disabled || !(year && quarter && quarterNumber)
              }
              startIcon={<CloudUploadIcon />}
            >
              {label}
            </Button>
            {transferring && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
            {error && <div className={classes.error}>{error}</div>}
          </div>
        </label>
      </FormControl>
    </div>
  )
}

QuarterlyUploadFileButton.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  context: PropTypes.string,
  callBack: PropTypes.func,
  inline: PropTypes.bool,
  maxQuarters: PropTypes.number,
}

export default QuarterlyUploadFileButton
